import { TextField } from "@mui/material";
import type { ChangeEvent, FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FormButtons from "@/components/FormButtons";
import YoutubePreview from "@/components/MediaPreview/YoutubePreview";
import type { AddMediaContentProps } from "@/components/MediaSelector/AddMediaModal";
import { Container } from "@/components/MediaSelector/forms/AddVimeoMedia";
import type { Media } from "@/declarations/models/Media";
import MediaType from "@/declarations/models/MediaType";
import { useMessenger } from "@/framework/Messenger/Messenger";
import { useLookup } from "@/hooks/useLookup";
import Api from "@/services/Api";

const validYouTubeUrlRegEx =
  /^(?:https?:\/\/(?:w{3}\.)?(?:youtube\.com\/(?:watch\?v=|v\/|embed\/)|youtu\.be\/))?([-_\w]+)$/;

export const AddYouTubeMedia: FC<AddMediaContentProps> = ({ value, ownerId, instanceId, onMediaSaved, onCancel }) => {
  const { t } = useTranslation("common");
  const touchedState = useLookup<boolean>({ url: false, name: false });
  const messenger = useMessenger();

  const [youtubeUrl, setYoutubeUrl] = useState<string>(value?.src || "");
  const [identifier, setIdentifier] = useState<string>(value?.identifier || "");
  const [name, setName] = useState<string>(value?.name || "");
  ownerId = value?.owner_id || ownerId;
  instanceId = value?.application_instance_id || instanceId;

  const urlError = !!touchedState.getItem("url") && !identifier;
  const nameError = !!touchedState.getItem("name") && !name;
  const validationError = !identifier || !name || (!ownerId && !instanceId);

  const handleUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value || "";
    const match = validYouTubeUrlRegEx.exec(url) || [];
    setYoutubeUrl(url);
    setIdentifier(match?.length === 2 ? match[1] : "");
  };

  const handleSave = async () => {
    const mediaToSave: Media = {
      identifier,
      name,
      media_type: MediaType.YOUTUBE,
      owner_id: ownerId!,
      application_instance_id: instanceId!,
    };
    let savedYoutubeMedia: Media | null;
    if (value?.id) {
      savedYoutubeMedia = await Api.updateMedia({
        ...value,
        ...mediaToSave,
      }).fetchDirect(null);
    } else {
      savedYoutubeMedia = await Api.createMedia(mediaToSave).fetchDirect(null);
    }
    if (savedYoutubeMedia) {
      onMediaSaved();
    } else {
      messenger.error("components.MediaSelector.AddYouTubeMedia.UnableToSaveYouTubeVideo");
    }
  };

  useEffect(() => {
    setYoutubeUrl(value?.src || "");
    setIdentifier(value?.identifier || "");
    setName(value?.name || "");
  }, [value]);

  return (
    <Container>
      <TextField
        label={t("components.MediaSelector.AddYouTubeMedia.YoutubeURL")}
        value={youtubeUrl}
        onChange={handleUrlChange}
        error={urlError}
        onFocus={() => touchedState.setItem("url", true)}
        required
        fullWidth
      />
      <TextField
        label={t("generic.name")}
        value={name}
        onChange={(e) => setName(e.target.value || "")}
        error={nameError}
        onFocus={() => touchedState.setItem("name", true)}
        required
        fullWidth
      />
      <YoutubePreview identifier={identifier} />
      <FormButtons disableSubmitButton={validationError} onCancel={onCancel} onSubmit={handleSave} />
    </Container>
  );
};

export default AddYouTubeMedia;
